import { Injectable, inject } from "@angular/core";
import { BaseService } from "../base.service";
import { HttpRestService } from "../http-client/http-rest-client.service";
import { NcpAvatar, NcpAvatarBatch, NcpAvatarCard, NcpAvatarCategoryListItem, NcpAvatarCategoryResponse } from "../../models/ncp-avatar/ncp-avatar.dto";

@Injectable({ providedIn: 'root' })
export class NcpAvatarApiService extends BaseService {
    private restService = inject(HttpRestService);


    public getCategories(): Promise<NcpAvatarCategoryListItem[]> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar`
        }));
    }

    public getCategory(categoryId: number): Promise<NcpAvatarCategoryResponse | null> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar/${categoryId}`
        }));
    }

    public getScaffold(): Promise<{ instruction: string }> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar/scaffold`
        }));
    }

    public upsertCategoryPrompt(categoryId: number, promptText: string): Promise<NcpAvatarCategoryResponse> {
        return this.handleResponse(this.restService.put({
            url: `/ncp-avatar/${categoryId}`,
            data: { PromptText: promptText }
        }));
    }

    public deleteCategoryPrompt(categoryId: number): Promise<boolean> {
        return this.handleResponse(this.restService.delete({
            url: `/ncp-avatar/${categoryId}`
        }));
    }

    public deleteManualOutfits(categoryId: number): Promise<boolean> {
        return this.handleResponse(this.restService.delete({
            url: `/ncp-avatar/${categoryId}/manual-outfits`
        }));
    }

    public generateForDeal(dealId: number, useDefaultMeasurements: boolean, chargeNcp: boolean, confirmRegenerate: boolean): Promise<boolean> {
        return this.handleResponse(this.restService.post({
            url: `/ncp-avatar-deal/${dealId}/generate?useDefaultMeasurements=${useDefaultMeasurements}&chargeNcp=${chargeNcp}&confirmRegenerate=${confirmRegenerate}`
        }));
    }

    public getChargeInfo(dealId: number): Promise<{ ncpChargeApplies: boolean }> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar-deal/${dealId}/charge-info`
        }));
    }

    public getDealBatchStatus(dealId: number): Promise<{ statusId: number | null; generatedCount: number }> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar-deal/${dealId}/batch-status`
        }));
    }

    public getPreviewBatch(categoryId?: number): Promise<NcpAvatarBatch | null> {
        const url = categoryId != null ? `/ncp-avatar/${categoryId}/preview-batch` : `/ncp-avatar/preview-batch`;
        return this.handleResponse(this.restService.get({ url }));
    }

    public retryPreview(categoryId?: number): Promise<boolean> {
        const url = categoryId != null ? `/ncp-avatar/${categoryId}/retry-preview` : `/ncp-avatar/retry-preview`;
        return this.handleResponse(this.restService.post({ url }));
    }

    public getDealCards(dealId: number): Promise<NcpAvatarCard[]> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar-deal/${dealId}/cards`
        }));
    }

    public getDealCardImages(dealId: number, cardIds: number[]): Promise<{ id: number; imageBase64: string }[]> {
        return this.handleResponse(this.restService.post({
            url: `/ncp-avatar-deal/${dealId}/cards/images`,
            data: cardIds
        }));
    }

    public downloadDealAvatarsZip(dealId: number): Promise<Blob> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar-deal/${dealId}/download`,
            responseType: 'blob' as 'json'
        }));
    }

    public deleteDealCard(dealId: number, cardId: number): Promise<boolean> {
        return this.handleResponse(this.restService.delete({
            url: `/ncp-avatar-deal/${dealId}/cards/${cardId}`
        }));
    }

    public deleteAllDealCards(dealId: number): Promise<boolean> {
        return this.handleResponse(this.restService.delete({
            url: `/ncp-avatar-deal/${dealId}/cards`
        }));
    }

    public generatePreview(): Promise<boolean> {
        return this.handleResponse(this.restService.post({
            url: `/ncp-avatar/generate-preview`
        }));
    }

    public generatePreviewForCategory(categoryId: number): Promise<boolean> {
        return this.handleResponse(this.restService.post({
            url: `/ncp-avatar/${categoryId}/generate-preview`
        }));
    }

    public getPreviewCards(): Promise<NcpAvatarCard[]> {
        return this.handleResponse(this.restService.get({
            url: `/ncp-avatar/cards`
        }));
    }

    public addNcpAvatar(categoryId: number, title: string, text: string): Promise<NcpAvatar> {
        return this.handleResponse(this.restService.post({
            url: `/ncp-avatar/${categoryId}/item`,
            data: { Title: title, Text: text }
        }));
    }

    public updateNcpAvatar(ncpAvatarId: number, title: string, text: string): Promise<NcpAvatar> {
        return this.handleResponse(this.restService.put({
            url: `/ncp-avatar/item/${ncpAvatarId}`,
            data: { Title: title, Text: text }
        }));
    }

    public deleteNcpAvatar(ncpAvatarId: number): Promise<void> {
        return this.handleResponse(this.restService.delete({
            url: `/ncp-avatar/item/${ncpAvatarId}`
        }));
    }

    public generateNcpAvatarPreview(ncpAvatarId: number): Promise<{ imageBase64: string | null }> {
        return this.handleResponse(this.restService.post({
            url: `/ncp-avatar/item/${ncpAvatarId}/generate-preview`,
            noErrorHandling: true
        }));
    }
}
